import type {
  Contact,
  EmailAndType,
  PhoneNumberAndType,
  Priority,
} from "../types";

export const PRIORITIES: Priority[] = ["low", "medium", "high", "urgent"];
export const DEFAULT_PRIORITY: Priority = "low";

/** Splits a full name typed in a single field into the contact name columns. */
export const splitFullName = (
  fullName: string,
): Pick<Contact, "first_name" | "last_name"> => {
  const [firstName = "", ...rest] = fullName.trim().split(/\s+/);
  return { first_name: firstName, last_name: rest.join(" ") };
};

export const getFullName = (
  contact: Partial<Pick<Contact, "first_name" | "last_name">>,
): string =>
  [contact.first_name, contact.last_name].filter(Boolean).join(" ").trim();

export const getPrimaryEmail = (emails?: EmailAndType[] | null): string =>
  emails?.find((entry) => entry.email)?.email ?? "";

export const getPrimaryPhone = (phones?: PhoneNumberAndType[] | null): string =>
  phones?.find((entry) => entry.number)?.number ?? "";

/** Replaces (or removes, when empty) the first e-mail, keeping the others. */
export const withPrimaryEmail = (
  emails: EmailAndType[] | null | undefined,
  email: string,
): EmailAndType[] => {
  const others = (emails ?? []).filter((entry) => entry.email).slice(1);
  const trimmed = email.trim().toLowerCase();
  return trimmed ? [{ email: trimmed, type: "Work" }, ...others] : others;
};

/** Replaces (or removes, when empty) the first phone number, keeping the others. */
export const withPrimaryPhone = (
  phones: PhoneNumberAndType[] | null | undefined,
  phone: string,
): PhoneNumberAndType[] => {
  const others = (phones ?? []).filter((entry) => entry.number).slice(1);
  const trimmed = phone.trim();
  return trimmed ? [{ number: trimmed, type: "Work" }, ...others] : others;
};

/** Parses an amount typed by the user ("1.500,50", "1500.5", "R$ 2000"). */
export const parseAmount = (value: string): number | null => {
  const cleaned = value.replace(/[^\d,.-]/g, "");
  if (!/\d/.test(cleaned)) return null;
  const separators = cleaned.match(/[.,]/g) ?? [];
  const lastSeparator = separators.at(-1);
  // With a single kind of separator, "1.500" / "1,500,000" are thousands, "1,5" is a decimal
  const isThousandsOnly =
    lastSeparator !== undefined &&
    new Set(separators).size === 1 &&
    (separators.length > 1 ||
      /^\d{3}$/.test(cleaned.slice(cleaned.lastIndexOf(lastSeparator) + 1)));
  const toAmount = (normalized: string) => {
    const amount = Number(normalized);
    return Number.isFinite(amount) ? Math.round(amount) : null;
  };
  if (lastSeparator === undefined) return toAmount(cleaned);
  if (isThousandsOnly) return toAmount(cleaned.replace(/[.,]/g, ""));
  const decimalIndex = cleaned.lastIndexOf(lastSeparator);
  return toAmount(
    `${cleaned.slice(0, decimalIndex).replace(/[.,]/g, "")}.${cleaned.slice(decimalIndex + 1)}`,
  );
};
